import apiService from './api.service';
import { baixarArquivo, lerBlob, nomeDoContentDisposition } from '@/utils/arquivo';
import type {
  ApontamentoReuniao,
  ApontamentosResponse,
  EquiplexDreResponse,
  EquiplexMaquinasResponse,
  EquiplexQualidadeResponse,
  EquiplexSopResponse,
  EquiplexVendasResponse,
  EquiplexVisaoGeralResponse,
  FiltroApontamentos,
  MetasResposta,
  NovaMeta,
  NovoApontamento,
  ProdutoCatalogo,
} from '@/types/dashboards';

/** Parâmetros do filtro dos painéis (`periodo`, `inicio`, `fim`, `calendario`, `produtos`). */
export type ParamsRecorte = Record<string, string>;

const APONTAMENTOS = '/dashboards/equiplex/apontamentos/';
const METAS = '/dashboards/equiplex/metas/';

/** Só os filtros preenchidos vão na query. */
function paramsDoFiltro(filtro: FiltroApontamentos): Record<string, string> {
  return Object.fromEntries(Object.entries(filtro).filter(([, v]) => !!v)) as Record<string, string>;
}

/** Erro de download chega como Blob; devolve o corpo em JSON para a tela mostrar a mensagem. */
async function erroDoCsv(erro: unknown): Promise<unknown> {
  const resposta = (erro as { response?: { data?: unknown } })?.response;
  if (!(resposta?.data instanceof Blob)) return erro;
  let corpo: unknown = {};
  try {
    corpo = JSON.parse(await lerBlob(resposta.data));
  } catch {
    corpo = {};
  }
  return { ...(erro as object), response: { ...resposta, data: corpo } };
}

/**
 * Painéis executivos (Equiplex). O backend serve o último snapshot gravado — a
 * leitura da réplica do Datasul acontece no agendamento (06:00) ou no botão
 * "Atualizar agora", nunca a cada abertura de tela.
 */
class DashboardsService {
  async getEquiplexVisaoGeral(params: ParamsRecorte = {}): Promise<EquiplexVisaoGeralResponse> {
    const { data } = await apiService.get<EquiplexVisaoGeralResponse>('/dashboards/equiplex/visao-geral/', { params });
    return data;
  }

  async getEquiplexMaquinas(params: ParamsRecorte = {}): Promise<EquiplexMaquinasResponse> {
    const { data } = await apiService.get<EquiplexMaquinasResponse>('/dashboards/equiplex/maquinas/', { params });
    return data;
  }

  async getEquiplexSop(params: ParamsRecorte = {}): Promise<EquiplexSopResponse> {
    const { data } = await apiService.get<EquiplexSopResponse>('/dashboards/equiplex/sop/', { params });
    return data;
  }

  /** Com filtro, o servidor monta a aba de novo a partir dos fatos do snapshot. */
  async getEquiplexQualidade(params: ParamsRecorte = {}): Promise<EquiplexQualidadeResponse> {
    const { data } = await apiService.get<EquiplexQualidadeResponse>('/dashboards/equiplex/qualidade/', { params });
    return data;
  }

  /** Catálogo do filtro de produto: todo produto que aparece em alguma aba. */
  async getProdutosEquiplex(): Promise<ProdutoCatalogo[]> {
    const { data } = await apiService.get<{ produtos: ProdutoCatalogo[] }>('/dashboards/equiplex/produtos/');
    return data.produtos;
  }

  async getEquiplexVendas(params: ParamsRecorte = {}): Promise<EquiplexVendasResponse> {
    const { data } = await apiService.get<EquiplexVendasResponse>('/dashboards/equiplex/vendas/', { params });
    return data;
  }

  async getEquiplexDre(params: ParamsRecorte = {}): Promise<EquiplexDreResponse> {
    const { data } = await apiService.get<EquiplexDreResponse>('/dashboards/equiplex/dre/', { params });
    return data;
  }

  /** Apontamentos da reunião do período (e da máquina, se pedida), mais as listas de motivo e turno. */
  async listarApontamentos(filtro: FiltroApontamentos = {}): Promise<ApontamentosResponse> {
    const { data } = await apiService.get<ApontamentosResponse>(APONTAMENTOS, { params: paramsDoFiltro(filtro) });
    return data;
  }

  async criarApontamento(dados: NovoApontamento): Promise<ApontamentoReuniao> {
    const { data } = await apiService.post<ApontamentoReuniao>(APONTAMENTOS, dados);
    return data;
  }

  async editarApontamento(id: string, dados: Partial<NovoApontamento>): Promise<ApontamentoReuniao> {
    const { data } = await apiService.patch<ApontamentoReuniao>(`${APONTAMENTOS}${id}/`, dados);
    return data;
  }

  /** Apagar só esconde: o histórico guarda o que era e quem apagou. */
  async apagarApontamento(id: string): Promise<void> {
    await apiService.delete(`${APONTAMENTOS}${id}/`);
  }

  /** CSV (abre no Excel) do mesmo filtro da lista. */
  async baixarApontamentosCsv(filtro: FiltroApontamentos = {}): Promise<void> {
    try {
      const resposta = await apiService.get<Blob>(`${APONTAMENTOS}csv/`, {
        params: paramsDoFiltro(filtro),
        responseType: 'blob',
      });
      baixarArquivo(resposta.data, nomeDoContentDisposition(resposta.headers, 'apontamentos_reuniao.csv'));
    } catch (erro) {
      throw await erroDoCsv(erro);
    }
  }

  /** A meta diária das sopradoras: a que vale hoje, a agendada e o histórico de ajustes. */
  async listarMetas(): Promise<MetasResposta> {
    const { data } = await apiService.get<MetasResposta>(METAS);
    return data;
  }

  /** Ajuste é sempre uma meta nova a partir de um dia; devolve o resumo atualizado. */
  async salvarMeta(dados: NovaMeta): Promise<MetasResposta> {
    const { data } = await apiService.post<MetasResposta>(METAS, dados);
    return data;
  }

  /** Atualiza todas as abas do painel de uma vez (um snapshot por aba). */
  async refreshEquiplex(): Promise<{ status: string }> {
    const { data } = await apiService.post<{ status: string }>('/dashboards/equiplex/refresh/');
    return data;
  }
}

export const dashboardsService = new DashboardsService();
